import { useState } from 'react';
import { Loader2, LogOut, RefreshCw, Check, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { api } from '@/api/apiClient';
import { useOdooAuth } from '@/lib/OdooAuthContext';
import { rememberCompany } from '@/lib/companySetup';
import CurrencyForm from '@/components/settings/CurrencyForm';
import PaymentMethodsEditor from '@/components/settings/PaymentMethodsEditor';

/**
 * First-time company setup (SellGo's own database). Before anyone works in
 * the app, the manager picks the company's currency and VAT rate, then its
 * payment methods. Everyone else waits for the manager.
 */
const STEPS = ['العملة والضريبة', 'وسائل الدفع'];

export default function CompanySetup({ company, failed, onDone }) {
  const { logoutOdoo } = useOdooAuth();
  const [step, setStep] = useState(1);
  const [current, setCurrent] = useState(null);   // the company as saved in step 1
  const [finishing, setFinishing] = useState(false);

  const finish = async () => {
    setFinishing(true);
    try {
      const res = await api.post('/sellgo_company_settings', { setup_done: true });
      rememberCompany(res.data);
      toast.success('الشركة جاهزة للشغل');
      onDone();
    } catch (e) {
      toast.error(e?.message || 'تعذّر الحفظ');
      setFinishing(false);
    }
  };

  const shell = (children) => (
    <div className="min-h-screen bg-background flex flex-col" dir="rtl">
      <header className="bg-card border-b border-border px-4 py-3 flex items-center gap-3">
        <img src="/icons/icon-192.png" alt="" className="w-9 h-9 rounded-xl" />
        <div className="flex-1 min-w-0">
          <p className="font-black text-foreground leading-tight">إعداد الشركة</p>
          {company?.name && <p className="text-xs text-muted-foreground truncate">{company.name}</p>}
        </div>
        <button onClick={logoutOdoo}
          className="text-xs text-muted-foreground flex items-center gap-1 px-2 py-1.5 rounded-lg hover:bg-muted">
          <LogOut className="w-3.5 h-3.5" /> تسجيل الخروج
        </button>
      </header>
      <main className="flex-1 w-full max-w-lg mx-auto p-4">{children}</main>
    </div>
  );

  if (!company) {
    return shell(failed ? (
      <div className="flex flex-col items-center text-center gap-4 py-16">
        <p className="font-semibold text-foreground">تعذّر تحميل بيانات الشركة — تأكد من الإنترنت</p>
        <Button variant="outline" onClick={onDone} className="gap-2"><RefreshCw className="w-4 h-4" /> إعادة المحاولة</Button>
      </div>
    ) : (
      <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
    ));
  }

  if (!company.can_edit) {
    return shell(
      <div className="flex flex-col items-center text-center gap-4 py-16">
        <div className="w-16 h-16 bg-amber-100 rounded-2xl flex items-center justify-center text-3xl">⏳</div>
        <div className="space-y-1">
          <p className="font-bold text-foreground">الشركة لسه بتتجهز</p>
          <p className="text-sm text-muted-foreground">
            مدير الشركة لازم يختار العملة ووسائل الدفع الأول، وبعدها تقدر تبدأ الشغل.
          </p>
        </div>
        <Button variant="outline" onClick={onDone} className="gap-2"><RefreshCw className="w-4 h-4" /> تحديث</Button>
      </div>
    );
  }

  return shell(
    <div className="space-y-5">
      {/* Steps */}
      <div className="flex gap-1.5">
        {STEPS.map((label, i) => (
          <div key={label} className="flex-1 space-y-1">
            <div className={`h-1.5 rounded-full ${step > i ? 'bg-primary' : 'bg-muted'}`} />
            <p className={`text-xs text-center ${step === i + 1 ? 'text-primary font-semibold' : 'text-muted-foreground'}`}>{label}</p>
          </div>
        ))}
      </div>

      {step === 1 && (
        <>
          <div>
            <h1 className="text-xl font-bold text-foreground">أهلاً بيك في SellGo 👋</h1>
            <p className="text-sm text-muted-foreground mt-1">
              قبل ما تبدأ، اختار عملة شركتك ونسبة الضريبة. تقدر تغيّرهم بعدين من الإعدادات.
            </p>
          </div>
          <CurrencyForm company={current || company} firstSetup preselect={!!current} submitLabel="التالي"
            onSaved={(d) => { setCurrent(d); setStep(2); }} />
        </>
      )}

      {step === 2 && (
        <>
          <div>
            <h1 className="text-xl font-bold text-foreground">وسائل الدفع</h1>
            <p className="text-sm text-muted-foreground mt-1">
              علّمنا لك الوسائل المعتادة لعملتك — زوّد أو شيل براحتك، وتقدر تغيّرها بعدين من الإعدادات.
            </p>
          </div>
          <PaymentMethodsEditor />
          <div className="sticky bottom-0 bg-background/95 backdrop-blur-sm py-3 flex gap-2">
            <Button variant="outline" onClick={() => setStep(1)} disabled={finishing} className="gap-1">
              <ChevronRight className="w-4 h-4" /> رجوع
            </Button>
            <Button onClick={finish} disabled={finishing} className="flex-1 bg-primary gap-2 h-11">
              {finishing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
              ابدأ العمل
            </Button>
          </div>
        </>
      )}
    </div>
  );
}
